import React, { useState } from 'react';
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid
} from 'recharts';
import { ChevronDown, ChevronUp, ExternalLink, Target, Zap, TrendingUp, Code2, Database, Layers } from 'lucide-react';
import { BAA_AND_BEAN_DATA, AUTOMOBILES_DATA } from '../constants/content';
import { FloatingAdinkra, KenteClothStrip } from '../components/Decorators';
import { RevealOnScroll } from '../hooks/useScrollReveal';

/* ─── Detailed Case Studies ─── */
const CASE_STUDIES = [
  {
    id: 'agl-ops',
    title: 'AGL Ops',
    subtitle: 'Enterprise Operations Platform',
    category: 'Operations & Engineering',
    status: 'Live',
    year: '2026',
    client: 'Automobiles Ghana Ltd',
    icon: Layers,
    heroDescription:
      'A comprehensive operations management platform combining a real-time Command Center dashboard with a mobile-first Staff Hub PWA. Built from scratch to replace spreadsheets, WhatsApp threads, and tribal knowledge with a single source of truth across sales, workshop, inventory, and finance.',
    problem:
      'Automobiles Ghana was scaling fast — but every branch ran on its own spreadsheets, supervisors chased updates by phone, and leadership had no real-time visibility into revenue, staff productivity, or inventory. Decisions took days. Errors took weeks to surface.',
    approach:
      'I designed two connected applications on the same data spine. The Command Center gives leadership 14 dedicated dashboard pages — finance, sales, workshop, inventory, staff, compliance. The Staff Hub is a PWA installable on any phone: technicians clock in, log tasks, and close work orders without logging into anything heavy. Google Sheets remains the source of record so finance never loses its paper trail.',
    outcome:
      '125% daily revenue growth in 8 weeks. Staff productivity visible in real-time. 30+ automated Playwright tests mean I can ship changes weekly without breaking production flows.',
    features: [
      'Live financial dashboard with real-time KPIs, revenue trends, and payment mix analytics',
      'Role-based Staff Hub PWA with clock-in/out, task logging, and work assignments',
      'Workshop & maintenance tracking with mechanic assignment and supervisor oversight',
      'Multi-channel sales tracking — Walk-In, WhatsApp, Phone, Social, Wholesale',
      'Inventory & POS system with low-stock alerts and warehouse transfers',
      'Comprehensive reporting suite with CSV/PDF export and Google Sheets sync',
      'Role-based access control (Admin, Manager, Supervisor, Staff)',
      '30+ Playwright end-to-end tests covering every critical user flow',
    ],
    tech: ['JavaScript', 'HTML5', 'CSS3', 'Chart.js', 'Playwright', 'PWA', 'Google Sheets API', 'Service Workers'],
    metrics: [
      { value: '14+', label: 'Dashboard Pages' },
      { value: '30+', label: 'Automated Tests' },
      { value: '125%', label: 'Revenue Growth' },
      { value: '2', label: 'Connected Apps' },
    ],
  },
  {
    id: 'avenue-lincoln',
    title: 'Avenue Lincoln',
    subtitle: 'Luxury Villa Booking Platform',
    category: 'Full-Stack Web',
    status: 'Deployed',
    year: '2025',
    client: 'Adebi Villa · Ridge, Accra',
    icon: Code2,
    heroDescription:
      'A full-stack luxury villa booking platform for a 3-bedroom property two streets from the British High Commission. Elegant responsive frontend, booking flow with date validation, admin dashboard — deployed and taking real reservations.',
    problem:
      'A high-end property was losing international bookings because its only channels were Airbnb (squeezing margins) and word-of-mouth. The owner needed a direct-booking site that felt as luxurious as the property itself, handled international guests, and gave a clean back-office view of reservations.',
    approach:
      'Built from scratch with Node.js + Express. The frontend emphasizes sensory cues — hero animations, sparkle effects, smooth scrolling — matched to the property\'s positioning. The booking flow validates dates, calculates stay duration, applies long-stay discounts up to 25%, and stores everything in a lightweight admin endpoint. No heavy CMS — shipping speed was the priority.',
    outcome:
      'Live platform with 7 content sections, end-to-end booking flow, and admin dashboard. Serves as the template for the Real Estate Management Hub pipeline venture.',
    features: [
      'Online booking with date validation, guest count, and auto-calculated stay duration',
      'Admin booking dashboard via REST API with complete guest information',
      'Responsive design with hero animations, sparkle canvas effects, and smooth scrolling',
      'Interactive statistics bar showcasing property highlights',
      'Comprehensive amenities showcase with tiered pricing (up to 25% long-stay discount)',
      'Mobile-first navigation across 7 content sections',
      'Web3Forms integration for direct concierge contact',
    ],
    tech: ['Node.js', 'Express.js', 'HTML5', 'CSS3', 'JavaScript', 'Web3Forms', 'Netlify'],
    metrics: [
      { value: '7', label: 'Content Sections' },
      { value: 'E2E', label: 'Booking Flow' },
      { value: '25%', label: 'Max Discount' },
      { value: '3', label: 'Bedrooms' },
    ],
  },
  {
    id: 'sales-analysis',
    title: 'Restaurant Sales Insights',
    subtitle: 'Full-Year Data Analysis',
    category: 'Data Analytics',
    status: 'Complete',
    year: '2024',
    client: 'Baa & Bean Café',
    icon: Database,
    heroDescription:
      'A full-year Python + Pandas analysis of 365 days of restaurant sales data. From raw transactions to executive-ready recommendations — this is the engagement that taught me how much story hides in a spreadsheet.',
    problem:
      'Management knew some days were better than others but couldn\'t explain why. They suspected weekends mattered, thought holidays mattered, and had no data on day-to-day variance. Decisions on staffing, prep, and promotions were guesses.',
    approach:
      'I pulled a full year of daily sales data, cleaned it with Pandas, and ran time-series analysis with a 7-day moving average to strip noise from signal. Layered in day-of-week patterns, holiday impact study, and statistical profiling. Packaged the findings in an executive PowerPoint with 8 charts and 5 concrete recommendations.',
    outcome:
      'Identified peak sales day (GH₵792), quantified weekend uplift, and proved that holidays did NOT reliably boost sales — against management\'s assumption. Recommendations drove the subsequent Baa & Bean operations engagement that delivered 120% revenue growth.',
    features: [
      'Time series analysis with 7-day moving average',
      'Peak performance analysis — highest sales day GH₵792, monthly patterns',
      'Day-of-week analysis proving consistent weekend outperformance',
      'Holiday impact study — debunked the "holidays always spike" assumption',
      'Statistical profiling with mean, standard deviation, distribution analysis',
      'Executive PowerPoint with 8 charts and actionable recommendations',
      'Clean, reproducible Jupyter notebook as the analytical artifact',
    ],
    tech: ['Python', 'Pandas', 'NumPy', 'Matplotlib', 'Jupyter', 'PowerPoint'],
    metrics: [
      { value: '365', label: 'Days Analyzed' },
      { value: '8', label: 'Visualizations' },
      { value: 'GH₵792', label: 'Peak Day' },
      { value: '5', label: 'Recommendations' },
    ],
  },
];

const statusColors = {
  Live: { bg: '#C9A84C', color: '#111', border: 'none' },
  Deployed: { bg: 'transparent', color: '#E5B947', border: '1.5px solid #E5B947' },
  Complete: { bg: 'rgba(26,26,26,0.6)', color: '#F5F0E8', border: '1px solid rgba(201,168,76,0.4)' },
};

/* ─── Case Study Card ─── */
const CaseStudy = ({ project, index }) => {
  const [showFeatures, setShowFeatures] = useState(false);
  const Icon = project.icon;
  const badge = statusColors[project.status] || statusColors.Complete;

  return (
    <RevealOnScroll delay={index * 120} direction={index % 2 === 0 ? 'left' : 'right'}>
      <article className="case-study">
        {/* Header */}
        <header className="case-study-header">
          <div className="case-study-icon">
            <Icon size={26} color="var(--gold)" />
          </div>
          <div className="case-study-heading">
            <div className="case-study-meta">
              <span className="case-category">{project.category}</span>
              <span className="case-year">{project.year}</span>
              <span className="case-client">· {project.client}</span>
            </div>
            <h3 className="case-title">{project.title}</h3>
            <p className="case-subtitle">{project.subtitle}</p>
          </div>
          <span className="case-status-badge" style={{ background: badge.bg, color: badge.color, border: badge.border }}>
            {project.status}
          </span>
        </header>

        {/* Hero description */}
        <p className="case-hero-desc">{project.heroDescription}</p>

        {/* Metrics strip */}
        <div className="case-metrics-strip">
          {project.metrics.map((m) => (
            <div key={m.label} className="case-metric">
              <div className="case-metric-value">{m.value}</div>
              <div className="case-metric-label">{m.label}</div>
            </div>
          ))}
        </div>

        {/* Problem / Approach / Outcome */}
        <div className="case-narrative">
          <div className="case-block">
            <div className="case-block-header">
              <Target size={16} color="var(--gold)" />
              <span className="case-block-label">The Problem</span>
            </div>
            <p>{project.problem}</p>
          </div>
          <div className="case-block">
            <div className="case-block-header">
              <Zap size={16} color="var(--gold)" />
              <span className="case-block-label">The Approach</span>
            </div>
            <p>{project.approach}</p>
          </div>
          <div className="case-block">
            <div className="case-block-header">
              <TrendingUp size={16} color="var(--gold)" />
              <span className="case-block-label">The Outcome</span>
            </div>
            <p>{project.outcome}</p>
          </div>
        </div>

        {/* Tech stack */}
        <div className="case-tech-row">
          {project.tech.map((t) => (
            <span key={t} className="tech-badge">{t}</span>
          ))}
        </div>

        {/* Expandable features */}
        <button className="project-expand-btn" onClick={() => setShowFeatures(!showFeatures)}>
          {showFeatures ? 'Hide Full Feature List' : `View All ${project.features.length} Features`}
          {showFeatures ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>

        {showFeatures && (
          <ul className="case-features-list">
            {project.features.map((f, i) => (
              <li key={i}>{f}</li>
            ))}
          </ul>
        )}

        {project.link && (
          <a href={project.link} target="_blank" rel="noopener noreferrer" className="project-link">
            Visit Live Site <ExternalLink size={12} />
          </a>
        )}
      </article>
    </RevealOnScroll>
  );
};

const ProjectsPage = () => (
  <div className="kente-dark-bg" style={{ padding: '5rem 2rem', position: 'relative' }}>
    <FloatingAdinkra count={6} color="rgba(229, 185, 71, 0.06)" />
    <div style={{ maxWidth: '1150px', margin: '0 auto', position: 'relative', zIndex: 2 }}>

      <RevealOnScroll>
        <div className="section-header">
          <h2 className="section-title">Selected Work</h2>
          <p className="section-subtitle">
            Case studies from the field — operations platforms, booking systems, and data engagements that moved the needle.
          </p>
        </div>
        <div className="kente-border"></div>
      </RevealOnScroll>

      {/* Case studies */}
      <div className="case-studies-stack">
        {CASE_STUDIES.map((project, index) => (
          <CaseStudy key={project.id} project={project} index={index} />
        ))}
      </div>

      <KenteClothStrip height={28} className="projects-kente-divider" />

      {/* Impact stories — the charts */}
      <RevealOnScroll>
        <div className="section-header" style={{ marginTop: '4rem' }}>
          <h3 className="section-title" style={{ fontSize: '1.6rem' }}>Impact Stories</h3>
          <p className="section-subtitle">
            Real data from two consulting engagements that shaped how I think about operations.
          </p>
        </div>
      </RevealOnScroll>

      <div className="impact-charts-grid" style={{ marginTop: '1.5rem' }}>
        <RevealOnScroll delay={0} direction="left">
          <div className="impact-chart-card" style={{ background: 'rgba(17,17,17,0.88)', borderColor: 'rgba(229,185,71,0.25)' }}>
            <div className="impact-chart-headline">
              <h4>Baa & Bean Café</h4>
              <span className="impact-chart-growth">+120% Revenue</span>
            </div>
            <p style={{ marginBottom: '0.75rem', color: 'var(--text-light-secondary)', fontSize: '0.82rem', lineHeight: 1.6 }}>
              GH₵12k → GH₵31.5k monthly over 6 months. SOP rewrites, workflow optimization, and data-driven menu decisions.
            </p>
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={BAA_AND_BEAN_DATA}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(229,185,71,0.15)" />
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#F5F0E8' }} />
                <YAxis tick={{ fontSize: 10, fill: '#F5F0E8' }} />
                <Tooltip contentStyle={{ background: '#1A1A1A', border: '1px solid rgba(229,185,71,0.4)', color: '#F5F0E8', fontSize: '0.75rem' }} formatter={(v) => [`GH₵${v.toLocaleString()}`, 'Revenue']} />
                <Area type="monotone" dataKey="target" stroke="rgba(229,185,71,0.35)" fill="none" strokeDasharray="5 5" name="Target" />
                <Area type="monotone" dataKey="revenue" stroke="#E5B947" fill="rgba(229,185,71,0.18)" name="Revenue" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </RevealOnScroll>
        <RevealOnScroll delay={200} direction="right">
          <div className="impact-chart-card" style={{ background: 'rgba(17,17,17,0.88)', borderColor: 'rgba(229,185,71,0.25)' }}>
            <div className="impact-chart-headline">
              <h4>Automobiles Ghana</h4>
              <span className="impact-chart-growth">+125% Daily</span>
            </div>
            <p style={{ marginBottom: '0.75rem', color: 'var(--text-light-secondary)', fontSize: '0.82rem', lineHeight: 1.6 }}>
              GH₵1,200 → GH₵2,700 daily sales in 8 weeks. Data-driven pricing, pipeline redesign, and customer engagement.
            </p>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={AUTOMOBILES_DATA}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(229,185,71,0.15)" />
                <XAxis dataKey="week" tick={{ fontSize: 10, fill: '#F5F0E8' }} />
                <YAxis yAxisId="left" tick={{ fontSize: 10, fill: '#F5F0E8' }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: '#F5F0E8' }} />
                <Tooltip contentStyle={{ background: '#1A1A1A', border: '1px solid rgba(229,185,71,0.4)', color: '#F5F0E8', fontSize: '0.75rem' }} />
                <Bar yAxisId="left" dataKey="sales" fill="#E5B947" name="Daily Sales (GH₵)" radius={[3, 3, 0, 0]} />
                <Bar yAxisId="right" dataKey="engagement" fill="rgba(229,185,71,0.4)" name="Engagement %" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </RevealOnScroll>
      </div>
    </div>
  </div>
);

export default ProjectsPage;
